import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { Ai, Cross } from '@factorialco/f0-react/icons/app'
import { Features } from 'generated/featureDefinitions'
import { ShiftManagementAutoScheduleStatusDocument } from 'generated/resources/graphql'

import Popover from 'modules/core/components/PopoverMenu'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { AutoScheduleModal } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Body/AutoScheduleModal'
import { GenerateCoverageModal } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Body/GenerateCoverage'
import { NoCoverageModal } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/Body/NoCoverageModal'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useUnassignedShifts } from 'modules/shift_management/hooks/useUnassignedShifts'
import { AutomaticScheduleStatus } from 'modules/shift_management/lib/consts/ShiftManagementAutomation'

const RunAutoScheduleButton = () => {
  const [showAutoScheduleModal, setShowAutoScheduleModal] = useState<boolean>(false)
  const [automationInProgress, setAutomationInProgress] = useState<boolean>(false)
  const [showGenerateCoverageModal, setShowGenerateCoverageModal] = useState<boolean>(false)
  const [showAutomationInProgressModal, setShowAutomationInProgressModal] = useState<boolean>(false)
  const [{ view }] = useSchedule()
  const [{ hasFeatureEntitlement }] = useEnabledFeatures()
  const isAutomationEnabled = hasFeatureEntitlement(Features.shift_management_automatic_planning)
  const [unassignedShifts] = useUnassignedShifts()
  const [{ locationIds }] = useFilters()
  const locationId = locationIds.at(0)

  const { data, startPolling, stopPolling } = useQuery(ShiftManagementAutoScheduleStatusDocument, {
    variables: {
      locationId: locationId ?? '',
      first: 1,
      sortOrder: { field: 'updated_at', order: 'desc' },
    },
    skip: !locationId,
  })

  useUnsafeEffect(() => {
    if (!data) {
      setAutomationInProgress(false)
      stopPolling()
      return
    }

    const status = data?.shiftManagement?.autoSchedulerExecutionsConnection?.nodes?.[0]?.status

    if (
      status === AutomaticScheduleStatus.SOLVING_ACTIVE ||
      status === AutomaticScheduleStatus.SOLVING_SCHEDULED
    ) {
      startPolling(3000)
      setAutomationInProgress(true)
    } else {
      stopPolling()
      setAutomationInProgress(false)
    }
  }, [data])

  return (
    <>
      {showAutoScheduleModal &&
        isAutomationEnabled &&
        (unassignedShifts.length > 0 ? (
          <AutoScheduleModal
            onClose={() => setShowAutoScheduleModal(false)}
            setIsSubscribed={setAutomationInProgress}
          />
        ) : (
          <NoCoverageModal
            onClose={() => setShowAutoScheduleModal(false)}
            handleGenerateCoverage={() => {
              setShowAutoScheduleModal(false)
              setShowGenerateCoverageModal(true)
            }}
          />
        ))}

      {showGenerateCoverageModal && isAutomationEnabled && (
        <GenerateCoverageModal
          onClose={() => setShowGenerateCoverageModal(false)}
          view={view}
          setShowAutoScheduleModal={setShowAutoScheduleModal}
        />
      )}
      <Popover type="list" open={showAutomationInProgressModal}>
        <Popover.Trigger>
          {!automationInProgress ? (
            <F0Button
              icon={Ai}
              label={i18n.t('shift_management.coverage_section.automation.button_title')}
              onClick={() => {
                track(trackingEvents.shiftManagement.autoSchedulerStart, {
                  from: 'coverage_section',
                })
                setShowAutoScheduleModal(true)
              }}
              size="md"
              variant="outline"
            />
          ) : (
            <Box
              flexDirection="row"
              alignItems="center"
              className="rounded-md"
              gap="s8"
              border={{ all: { style: 'solid', width: 's1', color: 'grey400' } }}
              style={{
                padding: '6px 12px 6px 8px',
              }}
              onClick={() => setShowAutomationInProgressModal(true)}
            >
              <Spinner size="small" />
              <Text.Normal weight="semibold" size="200" className="text-f1-foreground-primary">
                {i18n.t('shift_management.coverage_section.automation.in_progress.scheduling')}
              </Text.Normal>
            </Box>
          )}
        </Popover.Trigger>
        {showAutomationInProgressModal && automationInProgress && (
          <Popover.Content side="bottom" align="center" width="s300" paddingTop="s8">
            <Box
              paddingBottom="s8"
              paddingRight="s8"
              paddingLeft="s16"
              flexDirection="column"
              gap="s16"
              position="relative"
            >
              <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
                <Box style={{ marginTop: '8px' }}>
                  <Spinner size="medium" />
                </Box>
                <F0Button
                  hideLabel
                  icon={Cross}
                  variant="ghost"
                  label="Show automation in progress"
                  onClick={() => setShowAutomationInProgressModal(false)}
                />
              </Box>
              <Box gap="s0">
                <Text.Normal weight="semibold" size="200" className="text-lg text-f1-foreground">
                  {i18n.t('shift_management.coverage_section.automation.button_in_progress_new')}
                </Text.Normal>

                <Text.Normal weight="normal" size="200" className="text-f1-foreground-secondary">
                  {i18n.t(
                    'shift_management.coverage_section.automation.in_progress.description_new'
                  )}
                </Text.Normal>
              </Box>
            </Box>
          </Popover.Content>
        )}
      </Popover>
    </>
  )
}

export default RunAutoScheduleButton
